import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { getEquipmentGroupList } from '../../actions/equipment-group'
import { getFarmNotification, updateFarmNotification } from '../../actions/notification'
import { IDLE_MAX_TIME, MAX_ENGINE_RPM, MAX_PTO_RPM, SEMI_MAX_SPEED, TRUCK_MAX_SPEED } from '../../constants'
import { SubNav } from '../layout/submenu'
import { Icon, ToggleSwitch } from '../widgets'
import Select from "react-select";
import { SliderControl } from '../widgets/Notification'
import { usePermission } from '../../helpers/common'

const Notification = (props) => {

  const { farmId } = props;
  const dispatch = useDispatch();
  const allow = usePermission();

  const notification = useSelector(state => state.notification.configuration);
  const isRequesting = useSelector(state => state.notification.isRequesting);
  const isSaving = useSelector(state => state.notification.isSaving);
  const settings = useSelector(state => state.user.settings);
  const equipmentGroupList = useSelector(state => state.equipmentGroup.equipmentGroupList);

  const semiMaxSpeed = settings.speedShortName === 'mph' ? SEMI_MAX_SPEED : Math.round(SEMI_MAX_SPEED * 1.6);
  const truckMaxSpeed = settings.speedShortName === 'mph' ? TRUCK_MAX_SPEED : Math.round(TRUCK_MAX_SPEED * 1.6);
  const isAllowNotificationEdit = allow('notification-settings', 'edit');

  const [initialNotificationSettings, setInitialNotificationSettings] = useState(null);
  const [state, setState] = useState({
    enabled: notification.enabled,
    semiSlider: {
      step: 1,
      domain: [0, semiMaxSpeed],
      values: [notification.semi_max_speed.value],
      tickValues: [0, semiMaxSpeed / 2, semiMaxSpeed],
      enabled: notification.semi_max_speed.enabled
    },
    truckSlider: {
      step: 1,
      domain: [0, truckMaxSpeed],
      values: [notification.truck_max_speed.value],
      tickValues: [0, truckMaxSpeed / 2, truckMaxSpeed],
      enabled: notification.truck_max_speed.enabled
    },
    equipmentSlider: {
      step: 1,
      domain: [0, IDLE_MAX_TIME],
      values: [notification.idle_equipment_max_time.value],
      tickValues: [0, 30, 60, 90, 120],
      enabled: notification.idle_equipment_max_time.enabled,
      selectedEquipment: [],
    },
    ptoSlider: {
      step: 1,
      domain: [0, MAX_PTO_RPM],
      values: [notification.pto_rpm_max_speed.value],
      tickValues: [0, 250, 500, 750, 1000],
      enabled: notification.pto_rpm_max_speed.enabled
    },
    engineSlider: {
      step: 1,
      domain: [0, MAX_ENGINE_RPM],
      values: [notification.engine_rpm_max_speed.value],
      tickValues: [0, 625, 1250, 1875, 2500],
      enabled: notification.engine_rpm_max_speed.enabled
    },
    isEdited: false
  });
  useEffect(() => {
    dispatch(getEquipmentGroupList({ farmId }));
    dispatch(getFarmNotification({ farm_uuid: farmId }));
  }, [farmId]);

  const distinctEquipments = useMemo(() =>
    [...new Map(equipmentGroupList.map(item => [item['primary_category_id'], item])).values()]
      .map((eg) => { return ({ value: eg.primary_category_id, label: eg.primary_category_name }); })
    , [equipmentGroupList]);

  const selectedEquipments = useMemo(() => {
    if (notification.idle_equipment_max_time
      && notification.idle_equipment_max_time.equipment_category_id
      && notification.idle_equipment_max_time.equipment_category_id.length > 0) {
      return distinctEquipments.filter((it) => notification.idle_equipment_max_time.equipment_category_id.includes(it.value));
    }
    return [];
  }, [notification, distinctEquipments]);

  useEffect(() => {
    const data = {
      enabled: notification.enabled,
      semiSlider: {
        ...state.semiSlider,
        domain: [0, semiMaxSpeed],
        values: [settings.speedShortName === 'mph' ? notification.semi_max_speed.value : Math.round(notification.semi_max_speed.value * 1.6)],
        tickValues: [0, semiMaxSpeed / 2, semiMaxSpeed],
        enabled: notification.semi_max_speed.enabled
      },
      truckSlider: {
        ...state.truckSlider,
        domain: [0, truckMaxSpeed],
        values: [settings.speedShortName === 'mph' ? notification.truck_max_speed.value : Math.round(notification.truck_max_speed.value * 1.6)],
        tickValues: [0, truckMaxSpeed / 2, truckMaxSpeed],
        enabled: notification.truck_max_speed.enabled
      },
      equipmentSlider: {
        ...state.equipmentSlider,
        domain: [0, IDLE_MAX_TIME],
        values: [notification.idle_equipment_max_time.value],
        enabled: notification.idle_equipment_max_time.enabled,
        selectedEquipment: selectedEquipments,
      },
      ptoSlider: {
        ...state.ptoSlider,
        domain: [0, MAX_PTO_RPM],
        values: [notification.pto_rpm_max_speed.value],
        enabled: notification.pto_rpm_max_speed.enabled
      },
      engineSlider: {
        ...state.engineSlider,
        domain: [0, MAX_ENGINE_RPM],
        values: [notification.engine_rpm_max_speed.value],
        enabled: notification.engine_rpm_max_speed.enabled
      }
    }
    setState({ ...data });
    setInitialNotificationSettings({ ...data, isEdited: false });
  }, [isRequesting, notification, selectedEquipments]);

  const handleToggle = (notificationtype, enable) => {
    setState({ ...state, isEdited: true });
    if (notificationtype === 'semiSlider') setState({ ...state, isEdited: true, 'semiSlider': { ...state.semiSlider, enabled: enable } });
    else if (notificationtype === 'truckSlider') setState({ ...state, isEdited: true, 'truckSlider': { ...state.truckSlider, enabled: enable } });
    else if (notificationtype === 'equipmentSlider') setState({ ...state, isEdited: true, 'equipmentSlider': { ...state.equipmentSlider, enabled: enable } });
    else if (notificationtype === 'ptoSlider') setState({ ...state, isEdited: true, 'ptoSlider': { ...state.ptoSlider, enabled: enable } });
    else if (notificationtype === 'engineSlider') setState({ ...state, isEdited: true, 'engineSlider': { ...state.engineSlider, enabled: enable } });
    else if (notificationtype === 'notification') setState({ ...state, isEdited: true, enabled: enable });
  };

  const handleSliderUpdate = (sliderName, value) => {
    if (sliderName === 'semiSlider') setState({ ...state, isEdited: true, 'semiSlider': { ...state.semiSlider, values: [value] } });
    else if (sliderName === 'truckSlider') setState({ ...state, isEdited: true, 'truckSlider': { ...state.truckSlider, values: [value] } });
    else if (sliderName === 'equipmentSlider') setState({ ...state, isEdited: true, 'equipmentSlider': { ...state.equipmentSlider, values: [value] } });
    else if (sliderName === 'ptoSlider') setState({ ...state, isEdited: true, 'ptoSlider': { ...state.ptoSlider, values: [value] } });
    else if (sliderName === 'engineSlider') setState({ ...state, isEdited: true, 'engineSlider': { ...state.engineSlider, values: [value] } });
  };

  const { enabled, semiSlider, truckSlider, equipmentSlider, ptoSlider, engineSlider, isEdited } = state;

  const handleSave = () => {
    const equipmentCategoryId = equipmentSlider.selectedEquipment.map(item => { return item.value; });
    const semiMaxSpeedValue = settings.speedShortName === 'mph' ? semiSlider.values[0] : Math.round(semiSlider.values[0] / 1.6);
    const truckMaxSpeedValue = settings.speedShortName === 'mph' ? truckSlider.values[0] : Math.round(truckSlider.values[0] / 1.6);

    const params = {
      farm_uuid: farmId,
      notification_config: {
        enabled: enabled,
        semi_max_speed: {
          enabled: semiSlider.enabled,
          value: semiMaxSpeedValue
        },
        truck_max_speed: {
          enabled: truckSlider.enabled,
          value: truckMaxSpeedValue
        },
        idle_equipment_max_time: {
          enabled: equipmentSlider.enabled,
          value: equipmentSlider.values[0],
          equipment_category_id: equipmentCategoryId
        },
        pto_rpm_max_speed: {
          enabled: ptoSlider.enabled,
          value: ptoSlider.values[0]
        },
        engine_rpm_max_speed: {
          enabled: engineSlider.enabled,
          value: engineSlider.values[0]
        }
      }
    };
    const cb = () => {
      setState({ ...state, isEdited: false });
      dispatch(getFarmNotification({ farm_uuid: farmId }));
    };
    dispatch(updateFarmNotification(params, cb));
  };

  const handleCancel = () => {
    setState(initialNotificationSettings);
  };

  const handleSelectedEquipment = (value) => {
    setState({ ...state, isEdited: true, 'equipmentSlider': { ...state.equipmentSlider, selectedEquipment: value } });
  };

  return (
    <>
      <div className="header">
        <h6>Notification Configuration</h6>
        <ToggleSwitch
          className="notification-enable"
          style={{ cursor: isAllowNotificationEdit ? 'pointer' : 'not-allowed' }}
          title={enabled ? "Disable" : "Enable"}
          checked={enabled}
          disabled={!isAllowNotificationEdit}
          onChange={(e) => handleToggle("notification", e.target.checked)}
        />
      </div>
      <div className={`notification-container animate ${!isRequesting ? "animate__slideInUp" : ""} ${enabled ? '' : 'disabled not-allowed'}`} style={{ cursor: enabled ? '' : 'not-allowed' }} >
        {isRequesting && <SubNav.Loading />}
        {!isRequesting && <>
          <div className='d-flex notification-options'>
            <div className='notification-name'>
              Semi Max Speed {settings.speedShortName === 'mph' ? "(miles)" : "(km)"}
            </div>
            <div className='d-flex w-100'>
              <SliderControl
                step={semiSlider.step}
                domain={semiSlider.domain}
                values={semiSlider.values}
                tickValues={semiSlider.tickValues}
                onUpdate={(value) => handleSliderUpdate("semiSlider", value[0])}
                valueType={settings.speedShortName}
                enabled={semiSlider.enabled && isAllowNotificationEdit}
                className='w-80'
              />
              <ToggleSwitch
                title={semiSlider.enabled ? "Disable" : "Enable"}
                checked={semiSlider.enabled}
                onChange={(e) => handleToggle("semiSlider", e.target.checked)}
                className='notification-toggle'
                disabled={!isAllowNotificationEdit}
              />
            </div>
          </div>
          <div className='d-flex notification-options'>
            <div className='notification-name'>
              Truck Max Speed {settings.speedShortName === 'mph' ? "(miles)" : "(km)"}
            </div>
            <div className='d-flex w-100'>
              <SliderControl
                step={truckSlider.step}
                domain={truckSlider.domain}
                values={truckSlider.values}
                tickValues={truckSlider.tickValues}
                onUpdate={(value) => handleSliderUpdate("truckSlider", value[0])}
                valueType={settings.speedShortName}
                enabled={truckSlider.enabled && isAllowNotificationEdit}
              />
              <ToggleSwitch
                title={truckSlider.enabled ? "Disable" : "Enable"}
                checked={truckSlider.enabled}
                onChange={(e) => handleToggle("truckSlider", e.target.checked)}
                className='notification-toggle'
                disabled={!isAllowNotificationEdit}
              />
            </div>
          </div>
          <div className='d-flex w-100 notification-options idle-equipment'>
            <div className='notification-name'>
              Max Time For Idle Equipment
            </div>
            <div className='d-flex w-100 flex-column'>
              <div className='equipment-dropdown'>
                <Select
                  isDisabled={!isAllowNotificationEdit || !equipmentSlider.enabled}
                  value={equipmentSlider.selectedEquipment}
                  isMulti
                  name="colors"
                  options={distinctEquipments}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  onChange={(value) => handleSelectedEquipment(value)}
                />
              </div>
              <div className='d-flex idle-equipment-slider'>
                <SliderControl
                  step={equipmentSlider.step}
                  domain={equipmentSlider.domain}
                  values={equipmentSlider.values}
                  tickValues={equipmentSlider.tickValues}
                  onUpdate={(value) => handleSliderUpdate("equipmentSlider", value[0])}
                  valueType="min"
                  enabled={equipmentSlider.enabled && isAllowNotificationEdit}
                />
                <ToggleSwitch
                  className='notification-toggle'
                  title={equipmentSlider.enabled ? "Disable" : "Enable"}
                  checked={equipmentSlider.enabled}
                  onChange={(e) => handleToggle("equipmentSlider", e.target.checked)}
                  disabled={!isAllowNotificationEdit}
                />
              </div>
            </div>
          </div>
          <div className='d-flex notification-options' style={{ marginTop: "40px" }}>
            <div className='notification-name'>
              Max PTO RPM
            </div>
            <div className='d-flex w-100'>
              <SliderControl
                step={ptoSlider.step}
                domain={ptoSlider.domain}
                values={ptoSlider.values}
                tickValues={ptoSlider.tickValues}
                onUpdate={(value) => handleSliderUpdate("ptoSlider", value[0])}
                valueType="rpm"
                enabled={ptoSlider.enabled && isAllowNotificationEdit}
              />
              <ToggleSwitch
                title={ptoSlider.enabled ? "Disable" : "Enable"}
                checked={ptoSlider.enabled}
                onChange={(e) => handleToggle("ptoSlider", e.target.checked)}
                className='notification-toggle'
                disabled={!isAllowNotificationEdit}
              />
            </div>
          </div>
          <div className='d-flex notification-options'>
            <div className='notification-name'>
              Max Engine RPM
            </div>
            <div className='d-flex w-100'>
              <SliderControl
                step={engineSlider.step}
                domain={engineSlider.domain}
                values={engineSlider.values}
                tickValues={engineSlider.tickValues}
                onUpdate={(value) => handleSliderUpdate("engineSlider", value[0])}
                valueType="rpm"
                enabled={engineSlider.enabled && isAllowNotificationEdit}
              />
              <ToggleSwitch
                title={engineSlider.enabled ? "Disable" : "Enable"}
                checked={engineSlider.enabled}
                onChange={(e) => handleToggle("engineSlider", e.target.checked)}
                className='notification-toggle'
                disabled={!isAllowNotificationEdit}
              />
            </div>
          </div>
        </>}
      </div>
      {!isRequesting && isEdited && <div className='notification-button'>
        <Button variant='danger' className='button mr-2' size="sm" onClick={handleCancel}>
          <Icon className="fa fa-times" /> Cancel</Button>
        <Button variant='primary' className='button ml-2' size="sm" onClick={handleSave}>
          <Icon className="fa fa-floppy-o" /> {isSaving ? 'Saving...' : 'Save'}</Button>
      </div>}
    </>
  )
}

export default Notification